import { Injectable, signal, Signal, WritableSignal } from "@angular/core";

/**
 * Config for the form-builder header: editable title, status, actions.
 * The app component renders the bar; form-builder provides state and callbacks.
 */
export interface FormBuilderHeaderConfig {
  type: "form-builder";
  title: WritableSignal<string>;
  /** Called when user edits title in the header (so component can set hasUnsavedChanges) */
  onTitleChange?: (value: string) => void;
  formStatus: Signal<"draft" | "active">;
  hasUnsavedChanges: Signal<boolean>;
  saving: Signal<boolean>;
  isEditMode: Signal<boolean>;
  onBack: () => void;
  onCancel: () => void;
  onSave: () => void;
}

export type HeaderConfig = { type: "default" } | FormBuilderHeaderConfig;

/**
 * Shared header state so the app renders one top bar and only the content
 * changes per route (default title + actions vs form-builder title + Cancel/Save).
 */
@Injectable({ providedIn: "root" })
export class AppHeaderService {
  /** When null, app shows default header. When set, app shows that config (e.g. form-builder). */
  readonly config = signal<HeaderConfig | null>(null);

  setDefault(): void {
    this.config.set({ type: "default" });
  }

  setFormBuilderHeader(cfg: Omit<FormBuilderHeaderConfig, "type">): void {
    this.config.set({ type: "form-builder", ...cfg });
  }

  clear(): void {
    this.config.set(null);
  }
}
